import { NgModule, LOCALE_ID, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { RouterModuleResolverService } from './shared/services/router-module-resolver.service';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthCallbackComponent } from './auth-callback/auth-callback.component';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AuthSignoutCallbackComponent } from './auth-callback/auth-signout-callback';
import { DashboardModule } from './dashboard/dashboard.module';
import { CommonModule, registerLocaleData } from '@angular/common';
import { LayoutModule } from './layout/layout.module';
import { BaseComponent } from './shared/base.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ErrorHandler } from '@angular/core';
import { GlobalErrorHandler } from './shared/handler/global.error.handler';
import { TelemetryService } from './shared/services/telemetry.service';
import { BrowserModule } from '@angular/platform-browser';
import { FleetModule } from './fleet/fleet.module';
import { SilentRefreshComponent } from './silent-refresh/silent-refresh.component';
import { HttpInterceptorService } from 'src/app/services/http-interceptor.service';
import { NgxsModule } from '@ngxs/store';
import { environment } from 'src/environments/environment';
import { TenantState } from 'src/app/store/state/tenant.state';
import { AlertTypesState } from 'src/app/store/state/alert-types.state';
import { TenantSessionState } from 'src/app/store/state/tenant-session.state';
import { DynamicDialogModule } from './dynamic-dialog/dynamic-dialog.module';
import { DynamicTableModule } from './dynamic-table/dynamic-table.module';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsSelectSnapshotModule } from '@ngxs-labs/select-snapshot';
import { FilterManagerModule } from './filter-manager/filter-manager.module';
import { ToastGlobalComponent } from './layout/toast-global/toast-global.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { CustomMatPaginatorIntl } from './services/custom-mat-paginator-int';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import localeIt from '@angular/common/locales/it';
import localeEs from '@angular/common/locales/es-MX';
import localeZh from '@angular/common/locales/zh-Hans';
registerLocaleData(localeIt);
registerLocaleData(localeEs);
registerLocaleData(localeZh, 'zh-CN');
import { AngularMaterialModule } from './shared/module/angular-material.module';
import { CsvModule } from './shared/components/csv/csv.module';
import { AppConfigurationService } from './shared/services/app-configuration.service';
import { captureDeepLinkUrl } from './services/deep-link-capture';
import { NotFoundComponent } from './error-pages/not-found/not-found.component';
import { RegistrationComponent } from './registration/registration.component';
import { ApplicationPipesModule } from './shared/module/applicationPipesModule';
import { RouterModule } from '@angular/router';
import { FlagPickerModule } from './shared/flag-picker/flag-picker.module';
import { DrawerModule } from './shared/drawer/drawer.module';
import { languageVersion } from 'src/languageVersion';
import { WorkModule } from './work/work.module';
import { PendingChangesGuard } from './shared/candeactivate.guard';
import { PreviousRouteService } from './services/previous-route.service';
import { OutOfServiceModule } from './out-of-service/out-of-service.module';
import { DiagnosticArticleModule } from './diagnostics/component/diagnostic-article/diagnostic-article.module';
import { ToastComponent } from './layout/toast-global/toast/toast.component';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { CustomTranslateLoader } from './shared/custom-translate-loader';
import { AlertTriageOpener } from './service-center/alert-detail-dialog/alert-triage-opener';
import { AlertTriageDialogService } from './service-center/alert-detail-dialog/alert-triage-dialog.service';

// Factory function required during AOT compilation
export function httpTranslateLoaderFactory(http: HttpClient) {
  return new CustomTranslateLoader(
    http,
    './assets/i18n/',
    `.json?v=${languageVersion.version}`
  );
}

export function translateServiceFactory(translateService: TranslateService) {
  return translateService.currentLang !== undefined ? translateService.currentLang : "en-US";
}

@NgModule({ declarations: [
        AppComponent,
        AuthCallbackComponent,
        AuthSignoutCallbackComponent,
        BaseComponent,
        SilentRefreshComponent,
        ToastGlobalComponent,
        ToastComponent,
        NotFoundComponent,
        RegistrationComponent,
    ],
    bootstrap: [AppComponent], imports: [ApplicationPipesModule,
        CommonModule,
        BrowserModule,
        AngularMaterialModule,
        FormsModule,
        RouterModule.forRoot([]),
        AppRoutingModule,
        LayoutModule,
        DashboardModule,
        ReactiveFormsModule,
        BrowserAnimationsModule,
        FlagPickerModule,
        FleetModule,
        WorkModule,
        DrawerModule,
        OutOfServiceModule,
        NgxsModule.forRoot([TenantState, AlertTypesState, TenantSessionState], {
            developmentMode: environment.env === "local",
            // PT-001: NGXS builds string-path selector getters with new Function(), which a
            // Content-Security-Policy without 'unsafe-eval' blocks (@ngxs/store 21.0.0,
            // fastPropGetter). This switches it to compliantPropGetter, a plain loop.
            // No selector in this app currently takes the string path, so the switch is a
            // no-op today and a guard against one being added later.
            compatibility: { strictContentSecurityPolicy: true }
        }),
        NgxsSelectSnapshotModule.forRoot(),
        NgxsStoragePluginModule.forRoot({
            keys: ['TenantData']
        }),
        DynamicDialogModule,
        DynamicTableModule,
        CsvModule,
        FilterManagerModule,
        DiagnosticArticleModule,
        TranslateModule.forRoot({
            fallbackLang: 'en-US',
            loader: {
                provide: TranslateLoader,
                useFactory: httpTranslateLoaderFactory,
                deps: [HttpClient]
            }
        }),
        NgxMaskDirective], providers: [
        provideAppInitializer(() => {
        const initializerFn = (appInitializerFactory)(inject(AppConfigurationService));
        return initializerFn();
      }),
        provideAppInitializer(() => { inject(RouterModuleResolverService); }),
        provideAppInitializer(() => { inject(TelemetryService).init(); }),
        provideAppInitializer(() => captureDeepLinkUrl()),
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        { provide: HTTP_INTERCEPTORS, useClass: HttpInterceptorService, multi: true },
        { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
        { provide: AlertTriageOpener, useClass: AlertTriageDialogService },
        {
            provide: LOCALE_ID,
            deps: [TranslateService],
            useFactory: translateServiceFactory
        },
        PendingChangesGuard,
        PreviousRouteService,
        provideNgxMask(),
        provideHttpClient(withInterceptorsFromDi()),
        provideZoneChangeDetection({ eventCoalescing: true })
    ] })

export class AppModule { }

export function appInitializerFactory(appConfigurationService: AppConfigurationService) {
  return () => appConfigurationService.loadConfigs();
}
